import { getCompactionThreshold } from './compaction';
import { encodeScriptStringsForTransport } from './llm-transport';
import type { RateLimitPriority } from './rateLimitStore';
import {
    formatRateLimitMessage,
    getTaskPriority,
    getTimeUntilRetry,
    isRateLimited,
    setRateLimit,
} from './rateLimitStore';
import { awaitTurnstileToken, getTurnstileDiagnosticHeader } from './turnstileManager';
import type {
    CodeInterpreterFile,
    CodeInterpreterOutput,
    ConversationMessage,
    ImageContent,
    MessageContent,
    ReasoningBlock,
    TextContent,
    ToolCall,
    ToolDefinition,
    ToolMessage,
    Usage,
} from './types';
import {
    isAlwaysReasoningModel,
    isAssistantMessage,
    isOpenAIReasoningModel,
    isOpenRouterModel,
    isToolMessage,
    MessageRole,
    modelSupportsAdaptiveThinking,
    ReasoningEffort,
} from './types';

import { ErrorEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { isUndefined } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Cookies from 'js-cookie';
import { stringifyUnknown } from 'js/src/libs/custom-utils';

// Chat Completions reports total input in `prompt_tokens` — with BOTH the cached-read and the
// cache-write portions counted inside it — and breaks those out under `prompt_tokens_details` as
// `cached_tokens` and `cache_write_tokens`. Those are OpenAI's wire names and are deliberately NOT
// renamed to match our Usage type. We normalize to the convention the cost code assumes:
// `input_tokens` = fresh (uncached) input only, with each cache portion carried separately. Both
// have to come out of the total, or a token billed as a cache write (1.25x input on gpt-5.6+) is
// billed a second time as fresh input.
function normalizeOpenAIUsage(rawUsage: {
    prompt_tokens?: number | null;
    completion_tokens?: number | null;
    total_tokens?: number | null;
    prompt_tokens_details?: {
        cached_tokens?: number | null;
        cache_write_tokens?: number | null;
    } | null;
}): Usage {
    const promptTokens = rawUsage.prompt_tokens || 0;
    const cacheReadTokens = rawUsage.prompt_tokens_details?.cached_tokens || 0;
    const cacheWriteTokens = rawUsage.prompt_tokens_details?.cache_write_tokens || 0;
    return {
        input_tokens: Math.max(0, promptTokens - cacheReadTokens - cacheWriteTokens),
        output_tokens: rawUsage.completion_tokens || 0,
        total_tokens: rawUsage.total_tokens || 0,
        cache_read_input_tokens: cacheReadTokens,
        cache_creation_input_tokens: cacheWriteTokens,
    };
}

// The Responses API reports usage under different keys than Chat Completions:
// `input_tokens`/`output_tokens` (not prompt/completion) and the cache portions
// under `input_tokens_details`. Normalize to the same convention as
// normalizeOpenAIUsage so neither cache portion is billed as fresh input.
function normalizeResponsesUsage(rawUsage: {
    input_tokens?: number | null;
    output_tokens?: number | null;
    total_tokens?: number | null;
    input_tokens_details?: {
        cached_tokens?: number | null;
        cache_write_tokens?: number | null;
    } | null;
}): Usage {
    const inputTokens = rawUsage.input_tokens || 0;
    const cacheReadTokens = rawUsage.input_tokens_details?.cached_tokens || 0;
    const cacheWriteTokens = rawUsage.input_tokens_details?.cache_write_tokens || 0;
    return {
        input_tokens: Math.max(0, inputTokens - cacheReadTokens - cacheWriteTokens),
        output_tokens: rawUsage.output_tokens || 0,
        total_tokens: rawUsage.total_tokens || 0,
        cache_read_input_tokens: cacheReadTokens,
        cache_creation_input_tokens: cacheWriteTokens,
    };
}

export class ContextLengthExceededError extends Error {
    readonly type = 'context_length_exceeded' as const;

    constructor(message: string) {
        super(message);
        this.name = 'ContextLengthExceededError';
    }
}

export class StreamInterruptedError extends Error {
    readonly type = 'stream_interrupted' as const;

    constructor(message: string) {
        super(message);
        this.name = 'StreamInterruptedError';
    }
}

/**
 * The provider rejected the request after the proxy had already responded 200 OK,
 * so it arrives as an error event on the stream instead of an HTTP status. Carries
 * the provider's own message — usually the only clue as to which param it disliked.
 */
export class ProviderStreamError extends Error {
    readonly type = 'provider_error' as const;

    constructor(message: string) {
        super(message);
        this.name = 'ProviderStreamError';
    }

    // Every rejection is thrown from the same frame, so grouping by stack trace lumps an
    // outage, a revoked key and a policy flag into one issue. The provider's message
    // template tells them apart once the per-request parts are stripped.
    get sentryFingerprint(): string[] {
        const template = this.message
            .replace(/https?:\/\/\S+/g, '<url>')
            .replace(/\breq_[A-Za-z0-9]+/g, '<request-id>')
            .replace(/\d+/g, '<n>');
        return ['provider-stream-error', template];
    }
}

/**
 * Thrown when a request is blocked by the client-side rate limit throttle (either a
 * live 429 response or the preflight check against a previously stored limit). This is
 * expected, user-facing UX handled via a toast — callers should not report it as an
 * unexpected error (e.g. to Sentry).
 */
export class RateLimitError extends Error {
    readonly type = 'rate_limit' as const;

    constructor(message: string) {
        super(message);
        this.name = 'RateLimitError';
    }
}

/**
 * The proxy refused the request on content-policy grounds: either this turn's message was
 * flagged, or the user is blocked for repeated violations (`blocked`). Expected, user-facing
 * UX — callers render the message rather than reporting it to Sentry.
 */
export class ContentViolationError extends Error {
    readonly type = 'content_violation' as const;
    readonly blocked: boolean;

    constructor(message: string, blocked: boolean) {
        super(message);
        this.name = 'ContentViolationError';
        this.blocked = blocked;
    }
}

export interface LLMClientOptions {
    enableCodeInterpreter?: boolean;
    forceCodeInterpreter?: boolean;
    parallelToolCalls?: boolean;
    /**
     * Override the base URL for LLM API calls. Takes precedence over the
     * `OPENAI_BASE_URL` env var. Useful for tests/evals that want to bypass
     * the Chatterway proxy and hit the upstream provider directly.
     */
    baseUrl?: string;
    /** Reasoning effort, in the provider's own vocabulary. */
    reasoningEffort?: string;
    /**
     * Opt in to Anthropic adaptive thinking with a streamed, summarized
     * reasoning trace (Claude models that support it only). Off by default so
     * non-interactive Claude callers don't pay for thinking they never show.
     */
    enableThinkingSummaries?: boolean;
    /**
     * Responses API server-side compaction threshold in tokens. Defaults to a fraction of the
     * model's context limit (see compaction.ts).
     */
    compactionThreshold?: number;
}

function getCSRFToken(): string {
    const csrfToken = Cookies.get('csrftoken');
    return isUndefined(csrfToken) ? '' : csrfToken;
}

export function formatRetryTime(seconds: number): string {
    if (seconds < 60) {
        return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    } else if (seconds < 3600) {
        const minutes = Math.ceil(seconds / 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (seconds < 86400) {
        const hours = Math.ceil(seconds / 3600);
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
        const days = Math.ceil(seconds / 86400);
        return `${days} day${days !== 1 ? 's' : ''}`;
    }
}

function gpt5ReasoningConfig(
    model: string,
    options?: LLMClientOptions,
): { effort: string; verbosity: string } {
    const effort = options?.reasoningEffort || 'medium';
    if (effort === ReasoningEffort.NONE && isAlwaysReasoningModel(model)) {
        return { effort: ReasoningEffort.LOW, verbosity: 'medium' };
    }
    return { effort, verbosity: 'medium' };
}

/**
 * Whether the model is served by OpenAI itself, rather than another vendor behind
 * the same OpenAI-compatible proxy. Mirrors `_is_openai_model` in chatterway/views.py.
 * Note the `openai/` prefix belongs to Groq's gpt-oss models, not OpenAI.
 */
function isOpenAIVendorModel(model: string): boolean {
    if (model.startsWith('google/') || model.startsWith('openai/') || model.startsWith('claude-')) {
        return false;
    }
    return !model.startsWith('deepseek') && !model.includes('whisper');
}

/**
 * Chat Completions silently drops reasoning_effort whenever tools are present,
 * so a gpt-5.x caller that wants reasoning has to go through the Responses API
 * to get it. Reasoning off ('none' or unset) means the Chat Completions path.
 */
export function shouldUseResponsesApi(model: string, reasoningEffort?: string): boolean {
    if (isAlwaysReasoningModel(model)) {
        return true;
    }
    return (
        isOpenAIReasoningModel(model) &&
        !!reasoningEffort &&
        reasoningEffort !== ReasoningEffort.NONE
    );
}

export interface LLMResponse {
    content: string;
    tool_calls?: ToolCall[];
    /** Provider's reason for ending the turn, e.g. Anthropic's `max_tokens`. */
    stop_reason?: string;
    usage: Usage;
    code_interpreter_outputs?: CodeInterpreterOutput[];
    openai_response_id?: string;
    /** Raw thinking blocks (Anthropic) — preserved for replay on later turns. */
    thinking_blocks?: ReasoningBlock[];
    /** Raw `reasoning` items (Responses API) — replayed on later turns so the
     *  model keeps its chain-of-thought across the tool-call loop. */
    reasoning_items?: Array<Record<string, unknown>>;
    /** Raw `compaction` items (Responses API) — emitted when the server compacted
     *  the context during this turn; replayed in place of the input before them. */
    compaction_items?: Array<Record<string, unknown>>;
    /** Human-readable reasoning summary (Responses API, when summaries are on).
     *  For display/debugging — the encrypted trace for replay lives in
     *  reasoning_items. */
    reasoning_summary?: string;
}

// =============================================================================
// API Adapter Interface
// =============================================================================

export type ResponseFormat = { type: 'json_object' } | { type: 'text' };

/**
 * Whether any message that would remain in the Responses API `input` mentions "json".
 * Only the messages after the leading system run are considered, since those are what
 * survive instruction hoisting — which is the case this guards.
 */
function messagesMentionJson(messages: ConversationMessage[]): boolean {
    const firstNonSystemIdx = messages.findIndex((msg) => msg.role !== 'system');
    if (firstNonSystemIdx === -1) {
        return false;
    }
    return messages.slice(firstNonSystemIdx).some((msg) => {
        if (typeof msg.content === 'string') {
            return /json/i.test(msg.content);
        }
        if (Array.isArray(msg.content)) {
            return msg.content.some(
                (part) => part.type === 'text' && /json/i.test((part as TextContent).text),
            );
        }
        return false;
    });
}

interface APIAdapter {
    getEndpoint(): string;
    buildPayload(
        model: string,
        messages: ConversationMessage[],
        tools: ToolDefinition[] | undefined,
        task: string | undefined,
        streaming: boolean,
        baseUrl: string,
        options?: LLMClientOptions,
        responseFormat?: ResponseFormat,
    ): Record<string, unknown>;
    parseResponse(
        response: Response,
        streaming: boolean,
        onChunk?: (content: string) => void,
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse>;
}

// =============================================================================
// Chat Completions API Adapter
// =============================================================================

// OpenAI rejects any assistant message carrying more tool_calls than this.
const MAX_TOOL_CALLS_PER_ASSISTANT_MESSAGE = 128;

/**
 * Split any assistant turn whose parallel tool_calls exceed the per-message cap
 * into consecutive assistant messages of at most the cap, each immediately
 * followed by the tool results answering its calls. Without this, one oversized
 * turn 400s every subsequent request and the conversation is stuck.
 */
function splitOversizedToolCallTurns(messages: ConversationMessage[]): ConversationMessage[] {
    const result: ConversationMessage[] = [];
    let index = 0;
    while (index < messages.length) {
        const msg = messages[index];
        index++;
        if (
            !isAssistantMessage(msg) ||
            !msg.tool_calls ||
            msg.tool_calls.length <= MAX_TOOL_CALLS_PER_ASSISTANT_MESSAGE
        ) {
            result.push(msg);
            continue;
        }

        const toolRun: ToolMessage[] = [];
        while (index < messages.length) {
            const next = messages[index];
            if (!isToolMessage(next)) break;
            toolRun.push(next);
            index++;
        }

        const answeredIds = new Set<string>();
        for (
            let start = 0;
            start < msg.tool_calls.length;
            start += MAX_TOOL_CALLS_PER_ASSISTANT_MESSAGE
        ) {
            const chunkCalls = msg.tool_calls.slice(
                start,
                start + MAX_TOOL_CALLS_PER_ASSISTANT_MESSAGE,
            );
            const chunkIds = new Set(chunkCalls.map((call) => call.id));
            result.push({
                ...msg,
                content: start === 0 ? msg.content : '',
                tool_calls: chunkCalls,
            });
            for (const toolMessage of toolRun) {
                if (chunkIds.has(toolMessage.tool_call_id)) {
                    result.push(toolMessage);
                    answeredIds.add(toolMessage.tool_call_id);
                }
            }
        }
        result.push(...toolRun.filter((toolMessage) => !answeredIds.has(toolMessage.tool_call_id)));
    }
    return result;
}

export class ChatCompletionsAdapter implements APIAdapter {
    getEndpoint(): string {
        return '/chat/completions';
    }

    buildPayload(
        model: string,
        messages: ConversationMessage[],
        tools: ToolDefinition[] | undefined,
        task: string | undefined,
        streaming: boolean,
        baseUrl: string,
        options?: LLMClientOptions,
        responseFormat?: ResponseFormat,
    ): Record<string, unknown> {
        const payload: Record<string, unknown> = {
            model,
            messages: this.serializeMessages(messages),
            ...this.buildModelConfig(model, tools, options, baseUrl),
        };

        if (streaming) {
            payload.stream = true;
            // Without this, OpenAI's Chat Completions streaming API returns NO usage
            // data in the stream (every chunk has usage: null and there is no final
            // usage chunk), so token counting silently reads 0 for streamed turns.
            // It's an OpenAI-only param — Gemini and Groq route through the same
            // endpoint and reject it outright, so only send it to OpenAI models.
            if (isOpenAIVendorModel(model)) {
                payload.stream_options = { include_usage: true };
            }
        }

        if (task && baseUrl.includes('chatterway')) {
            payload.task = task;
        }

        if (tools && tools.length > 0) {
            payload.tools = tools;
            payload.tool_choice = 'auto';
        }

        if (responseFormat) {
            payload.response_format = responseFormat;
        }

        return payload;
    }

    /**
     * Serialize the whole conversation into Chat Completions messages.
     *
     * A tool result that returns images is split into a text-only `role:"tool"`
     * message plus a synthetic `role:"user"` message carrying the images (the
     * Chat Completions API rejects image parts inside a tool message). That
     * split can't be emitted inline: after an assistant message with
     * `tool_calls`, every tool response must appear contiguously with no other
     * role interleaved, otherwise the API rejects (or mishandles) the request.
     * With parallel tool calls, emitting the image `user` message immediately
     * after its tool result would wedge it between sibling tool responses. So we
     * defer image `user` messages until the run of consecutive tool messages
     * ends, then flush them together.
     */
    private serializeMessages(messages: ConversationMessage[]): Array<Record<string, unknown>> {
        const serialized: Array<Record<string, unknown>> = [];
        let deferredImageMessages: Array<Record<string, unknown>> = [];
        const flushDeferred = () => {
            if (deferredImageMessages.length > 0) {
                serialized.push(...deferredImageMessages);
                deferredImageMessages = [];
            }
        };

        for (const msg of splitOversizedToolCallTurns(messages)) {
            const isTool = isToolMessage(msg);
            // A non-tool message ends the current tool run: flush any images the
            // run's tool results produced before emitting it.
            if (!isTool) {
                flushDeferred();
            }
            const parts = this.serializeMessage(msg);
            if (isTool && parts.length > 1) {
                // parts[0] is the text-only tool message (stays contiguous);
                // the rest are image user messages, deferred past the tool run.
                serialized.push(parts[0]);
                deferredImageMessages.push(...parts.slice(1));
            } else {
                serialized.push(...parts);
            }
        }
        flushDeferred();

        return serialized;
    }

    /**
     * Serialize one conversation message into Chat Completions message(s).
     *
     * The Chat Completions API rejects image parts inside a `role: "tool"`
     * message ("tool message content only supports text content"). When a tool
     * returns images, we keep the tool message text-only and emit the images in
     * a following synthetic `role: "user"` message, which is the supported way
     * to feed images alongside a tool result. All other messages pass through
     * unchanged.
     */
    private serializeMessage(msg: ConversationMessage): Array<Record<string, unknown>> {
        if (isToolMessage(msg) && Array.isArray(msg.content)) {
            const textParts = msg.content.filter(
                (part): part is TextContent => part.type === 'text',
            );
            const imageParts = msg.content.filter(
                (part): part is ImageContent => part.type === 'image_url',
            );

            if (imageParts.length > 0) {
                const toolText = textParts.map((part) => part.text).join('\n');
                const toolMessage: Record<string, unknown> = {
                    role: msg.role,
                    content: toolText || '[Tool returned image(s); see the following message.]',
                    tool_call_id: msg.tool_call_id,
                };
                const userMessage: Record<string, unknown> = {
                    role: MessageRole.USER,
                    content: [
                        {
                            type: 'text',
                            text: `Images returned by tool result ${msg.tool_call_id}:`,
                        },
                        ...imageParts,
                    ],
                };
                return [toolMessage, userMessage];
            }
        }

        return [
            {
                role: msg.role,
                content: msg.content,
                ...(isAssistantMessage(msg) && msg.tool_calls
                    ? { tool_calls: msg.tool_calls }
                    : {}),
                ...(isToolMessage(msg) ? { tool_call_id: msg.tool_call_id } : {}),
            },
        ];
    }

    private buildModelConfig(
        model: string,
        tools?: ToolDefinition[],
        options?: LLMClientOptions,
        baseUrl = '',
    ): Record<string, unknown> {
        const isGoogleModel = model.startsWith('google/');
        const isReasoningModel = isOpenAIReasoningModel(model);
        const hasTools = tools && tools.length > 0;

        if (isReasoningModel) {
            // gpt-5.x / gpt-6.x reasoning models apply a default reasoning_effort server-side and
            // then 400 when function tools are present on Chat Completions ("...use /v1/responses or set
            // reasoning_effort to 'none'"). Chat Completions can't reason while calling tools, so
            // pin it to 'none' to keep tool-using turns working; reasoning + tools routes through
            // the Responses API instead.
            if (hasTools) return { reasoning_effort: 'none' };
            const { effort, verbosity } = gpt5ReasoningConfig(model, options);
            return { reasoning_effort: effort, verbosity };
        }

        // Gemini 3.x renamed thinking_budget -> thinking_level (minimal/low/medium/high).
        // See https://ai.google.dev/gemini-api/docs/whats-new-gemini-3.5
        const isGemini3 = isGoogleModel && model.includes('gemini-3');
        if (isGemini3) {
            return {
                google: {
                    thinking_config: {
                        thinking_level: options?.reasoningEffort || ReasoningEffort.MINIMAL,
                    },
                },
            };
        }

        // OpenRouter exposes a unified `reasoning` param that it normalizes per underlying model.
        // Fires for OpenRouter reasoning models routed through our chatterway proxy (which relays
        // `reasoning` on to OpenRouter), and for anything on a direct OpenRouter base URL (evals).
        // `none` disables thinking; low/medium/high map to effort; anything else (incl. unset) is
        // omitted so an unsupported value never reaches it.
        const effort = options?.reasoningEffort;
        const isGradedEffort =
            effort === ReasoningEffort.LOW ||
            effort === ReasoningEffort.MEDIUM ||
            effort === ReasoningEffort.HIGH;
        if (isOpenRouterModel(model) || baseUrl.includes('openrouter')) {
            if (effort === ReasoningEffort.NONE) {
                return { reasoning: { enabled: false } };
            }
            if (isGradedEffort) {
                return { reasoning: { effort } };
            }
        }

        return {};
    }

    async parseResponse(
        response: Response,
        streaming: boolean,
        onChunk?: (content: string) => void,
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        if (streaming) {
            return this.parseStreamingResponse(response, onChunk!, onThinking);
        }
        return this.parseJsonResponse(response, onThinking);
    }

    private async parseJsonResponse(
        response: Response,
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        const data = await response.json();
        const message = data.choices[0].message;

        // OpenRouter returns the model's thinking in a separate `reasoning` field; surface it so a
        // reasoning turn actually shows its thinking rather than just billing for it. (Native
        // gpt-5/gemini chat-completions turns don't populate it, so this is a no-op for them.)
        if (onThinking && typeof message.reasoning === 'string' && message.reasoning) {
            onThinking(message.reasoning);
        }

        const usage = normalizeOpenAIUsage(data.usage);
        return {
            content: message.content || '',
            tool_calls: message.tool_calls,
            usage:
                typeof data.provider === 'string' ? { ...usage, provider: data.provider } : usage,
        };
    }

    private async parseStreamingResponse(
        response: Response,
        onChunk: (content: string) => void,
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        if (!response.body) {
            throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedContent = '';
        let accumulatedReasoning = '';
        let toolCalls: ToolCall[] | undefined;
        let usage: Usage = {
            input_tokens: 0,
            output_tokens: 0,
            total_tokens: 0,
        };
        // OpenRouter names the upstream it routed to (e.g. "Together") on every chunk of a
        // response; captured here since it rides alongside `usage` rather than inside it.
        let provider: string | undefined;

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6).trim();
                        if (data === '[DONE]') {
                            continue;
                        }

                        try {
                            const chunk = JSON.parse(data);

                            // Check for stream error from backend
                            if (chunk.error?.type === 'stream_interrupted') {
                                throw new StreamInterruptedError(
                                    'Connection to AI provider was interrupted. Please try again.',
                                );
                            }
                            if (chunk.error) {
                                throw new ProviderStreamError(
                                    chunk.error.message ?? 'The AI provider rejected the request.',
                                );
                            }

                            if (typeof chunk.provider === 'string') {
                                provider = chunk.provider;
                            }

                            const delta = chunk.choices?.[0]?.delta;

                            if (delta?.content) {
                                accumulatedContent += delta.content;
                                onChunk(accumulatedContent);
                            }

                            // OpenRouter streams the model's thinking in a separate `reasoning`
                            // field; surface it so a reasoning turn shows its thinking as it goes.
                            if (typeof delta?.reasoning === 'string' && delta.reasoning) {
                                accumulatedReasoning += delta.reasoning;
                                onThinking?.(accumulatedReasoning);
                            }

                            if (delta?.tool_calls) {
                                if (!toolCalls) {
                                    toolCalls = [];
                                }
                                for (const toolCallDelta of delta.tool_calls) {
                                    const index = toolCallDelta.index;
                                    if (!toolCalls[index]) {
                                        toolCalls[index] = {
                                            id: toolCallDelta.id || '',
                                            type: 'function',
                                            function: {
                                                name: toolCallDelta.function?.name || '',
                                                arguments: toolCallDelta.function?.arguments || '',
                                            },
                                            // Gemini's thought_signature lives here and has to be
                                            // replayed verbatim next turn (see ToolCall).
                                            ...(toolCallDelta.extra_content
                                                ? { extra_content: toolCallDelta.extra_content }
                                                : {}),
                                        };
                                    } else {
                                        if (toolCallDelta.function?.name) {
                                            toolCalls[index].function.name +=
                                                toolCallDelta.function.name;
                                        }
                                        if (toolCallDelta.function?.arguments) {
                                            toolCalls[index].function.arguments +=
                                                toolCallDelta.function.arguments;
                                        }
                                        if (toolCallDelta.extra_content) {
                                            toolCalls[index].extra_content =
                                                toolCallDelta.extra_content;
                                        }
                                    }
                                }
                            }

                            if (chunk.usage) {
                                usage = normalizeOpenAIUsage(chunk.usage);
                            }
                        } catch (error) {
                            // Re-throw stream errors so they reach the user
                            if (
                                error instanceof StreamInterruptedError ||
                                error instanceof ProviderStreamError
                            ) {
                                throw error;
                            }
                            // Silently skip parse errors to avoid breaking the stream
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock();
        }

        return {
            content: accumulatedContent,
            tool_calls: toolCalls,
            usage: provider ? { ...usage, provider } : usage,
        };
    }
}

// =============================================================================
// Responses API Adapter
// =============================================================================

/**
 * Responses attaches server-only fields to reasoning/compaction output items — `status: null`
 * (the request schema types status as an enum that admits no null) and `created_by` (not part of
 * the input schema at all) — that get the request rejected if replayed verbatim.
 */
function withoutServerOnlyFields(item: Record<string, unknown>): Record<string, unknown> {
    const { created_by: _createdBy, ...withoutCreatedBy } = item;
    if (withoutCreatedBy.status !== null) {
        return withoutCreatedBy;
    }
    const { status: _status, ...withoutStatus } = withoutCreatedBy;
    return withoutStatus;
}

export class ResponsesAPIAdapter implements APIAdapter {
    getEndpoint(): string {
        return '/responses';
    }

    buildPayload(
        model: string,
        messages: ConversationMessage[],
        tools: ToolDefinition[] | undefined,
        task: string | undefined,
        streaming: boolean,
        baseUrl: string,
        options?: LLMClientOptions,
        responseFormat?: ResponseFormat,
    ): Record<string, unknown> {
        // OpenAI only accepts `text.format: json_object` when the word "json" appears in an
        // *input* message — it does not look at `instructions`. Our JSON-mode callers say it
        // in their system prompt, so hoisting that prompt out of the input would 400 the
        // request. Keep the messages inline unless the input already satisfies the check.
        const needsJsonInInput =
            responseFormat?.type === 'json_object' && !messagesMentionJson(messages);
        const { instructions, remainingMessages } = needsJsonInInput
            ? { instructions: '', remainingMessages: messages }
            : this.extractLeadingInstructions(messages);

        const payload: Record<string, unknown> = {
            model,
            input: this.buildInputItems(remainingMessages),
            ...this.buildModelConfig(model, options),
        };

        if (instructions) {
            payload.instructions = instructions;
        }

        if (streaming) {
            payload.stream = true;
        }

        if (task && baseUrl.includes('chatterway')) {
            payload.task = task;
        }

        const allTools: Array<Record<string, unknown>> = [];

        // Add function tools
        if (tools && tools.length > 0) {
            // Responses API uses flattened tool structure for functions
            for (const tool of tools) {
                allTools.push({
                    type: tool.type,
                    name: tool.function.name,
                    description: tool.function.description,
                    parameters: tool.function.parameters,
                    // Strict mode promotes every property to required, which would
                    // force the model to invent values for each tool's optional
                    // fields. Match Chat Completions and honor `required` as authored.
                    strict: false,
                });
            }
        }

        const include: string[] = [];
        if (options?.enableCodeInterpreter) {
            allTools.push({
                type: 'code_interpreter',
                container: { type: 'auto' },
            });
            // Include code interpreter outputs in the response
            include.push('code_interpreter_call.outputs');
        }
        // Ask for encrypted reasoning back so we can thread the model's own
        // reasoning items into the next request (keeps its chain-of-thought
        // across the tool-call loop; without it a reasoning model loops).
        // encrypted_content is only returned in stateless mode, so we opt out
        // of server-side storage — we re-thread reasoning items via `input`
        // ourselves rather than relying on previous_response_id.
        if (isOpenAIReasoningModel(model)) {
            include.push('reasoning.encrypted_content');
            payload.store = false;
            // Server-side compaction: once the rendered context crosses the threshold, the
            // server folds the older input into an opaque `compaction` output item. We keep
            // it on the assistant message and thread it back in place of that input.
            payload.context_management = [
                {
                    type: 'compaction',
                    compact_threshold:
                        options?.compactionThreshold ?? getCompactionThreshold(model),
                },
            ];
        }
        if (include.length > 0) {
            payload.include = include;
        }

        if (allTools.length > 0) {
            payload.tools = allTools;
            if (options?.forceCodeInterpreter) {
                payload.tool_choice = { type: 'code_interpreter' };
            } else {
                payload.tool_choice = 'auto';
            }
            // Default to false when code interpreter is enabled (need sequential execution
            // so model sees CI outputs before calling other tools), otherwise true
            const parallelDefault = !options?.enableCodeInterpreter;
            payload.parallel_tool_calls = options?.parallelToolCalls ?? parallelDefault;
        }

        if (responseFormat) {
            // The Responses API nests output-format config under `text.format` — unlike
            // Chat Completions' top-level `response_format`, which OpenAI's
            // AsyncResponses.create() rejects as an unknown kwarg.
            payload.text = { format: responseFormat };
        }

        return payload;
    }

    /**
     * Split off the base prompt (the leading run of system messages) so it can be
     * sent via the Responses API `instructions` field, which the model weights
     * above ordinary input. A system message appearing after any non-system
     * message is left in place — it's positional context, not a global directive.
     */
    private extractLeadingInstructions(messages: ConversationMessage[]): {
        instructions: string;
        remainingMessages: ConversationMessage[];
    } {
        const instructionParts: string[] = [];
        let firstNonSystemIdx = 0;
        for (const msg of messages) {
            if (msg.role !== 'system') {
                break;
            }
            if (typeof msg.content === 'string' && msg.content.length > 0) {
                instructionParts.push(msg.content);
            }
            firstNonSystemIdx++;
        }
        return {
            instructions: instructionParts.join('\n\n'),
            remainingMessages: messages.slice(firstNonSystemIdx),
        };
    }

    private buildInputItems(messages: ConversationMessage[]): Array<Record<string, unknown>> {
        const inputItems: Array<Record<string, unknown>> = [];

        for (const msg of messages) {
            if (isAssistantMessage(msg)) {
                // Replay this turn's compaction item first (it stands in for all the
                // input before it), then its reasoning items — they precede the
                // function calls in the model's output, and threading them back
                // keeps a reasoning model's chain-of-thought across the tool-call
                // loop. Dropping them makes it re-plan from scratch and loop.
                for (const item of msg.compaction_items ?? []) {
                    inputItems.push(withoutServerOnlyFields(item));
                }
                for (const item of msg.reasoning_items ?? []) {
                    inputItems.push(withoutServerOnlyFields(item));
                }

                // Skip code_interpreter_outputs when building input items
                // These outputs contain ephemeral file_ids/URLs that are only valid for the
                // current response. Sending them back in subsequent requests causes errors
                // because the URLs expire and OpenAI requires valid URLs for image outputs.

                if (msg.tool_calls && msg.tool_calls.length > 0) {
                    for (const toolCall of msg.tool_calls) {
                        inputItems.push({
                            type: 'function_call',
                            call_id: toolCall.id,
                            name: toolCall.function.name,
                            arguments: toolCall.function.arguments,
                        });
                    }
                }

                if (
                    msg.content &&
                    (typeof msg.content === 'string' ? msg.content : msg.content.length > 0)
                ) {
                    inputItems.push({
                        role: msg.role,
                        content: this.transformContent(msg.content),
                    });
                }
            } else if (isToolMessage(msg)) {
                // As of openai-python 2.x, function_call_output.output accepts a
                // multimodal array (input_text + input_image parts), so a tool's
                // images ride natively inside the tool result — no separate user
                // message. transformContent yields exactly those content parts.
                inputItems.push({
                    type: 'function_call_output',
                    call_id: msg.tool_call_id,
                    output: Array.isArray(msg.content)
                        ? this.transformContent(msg.content)
                        : msg.content,
                });
            } else {
                inputItems.push({
                    role: msg.role,
                    content: this.transformContent(msg.content),
                });
            }
        }

        return inputItems;
    }

    private transformContent(content: MessageContent): string | Array<Record<string, unknown>> {
        if (typeof content === 'string') {
            return content;
        }

        return content.map((part): Record<string, unknown> => {
            if (part.type === 'text') {
                return {
                    type: 'input_text',
                    text: part.text,
                };
            } else if (part.type === 'image_url') {
                return {
                    type: 'input_image',
                    image_url: part.image_url.url,
                    detail: 'auto',
                };
            }
            return part as unknown as Record<string, unknown>;
        });
    }

    private buildModelConfig(model: string, options?: LLMClientOptions): Record<string, unknown> {
        if (!isOpenAIReasoningModel(model)) {
            return {};
        }
        const { effort, verbosity } = gpt5ReasoningConfig(model, options);
        const reasoning: Record<string, unknown> = { effort };
        // Without summary:'auto' the reasoning items come back with an empty `summary`.
        if (options?.enableThinkingSummaries) {
            reasoning.summary = 'auto';
        }
        return { reasoning, text: { verbosity } };
    }

    async parseResponse(
        response: Response,
        streaming: boolean,
        onChunk?: (content: string) => void,
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        if (streaming) {
            return this.parseStreamingResponse(response, onChunk, onThinking);
        }
        return this.parseResponsePayload(await response.json(), onThinking);
    }

    /**
     * Streams the response for live UI feedback, then parses the `response.completed`
     * event — which carries the finished response verbatim — through the same
     * payload parser the non-streaming path uses. The deltas only drive onChunk /
     * onThinking; every value we return (tool calls, reasoning items to thread back,
     * usage) comes from that final payload, so the two paths can't drift.
     */
    private async parseStreamingResponse(
        response: Response,
        onChunk?: (content: string) => void,
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        if (!response.body) {
            throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedText = '';
        let accumulatedReasoning = '';
        let finalPayload: Record<string, unknown> | undefined;

        const handleEvent = (data: string) => {
            if (!data || data === '[DONE]') return;
            let parsed: Record<string, unknown>;
            try {
                parsed = JSON.parse(data);
            } catch {
                return;
            }

            const errorObj = parsed.error as { type?: string; message?: string } | undefined;
            if (errorObj?.type === 'stream_interrupted') {
                throw new StreamInterruptedError(
                    'Connection to AI provider was interrupted. Please try again.',
                );
            }
            if (errorObj) {
                throw new ProviderStreamError(
                    errorObj.message ?? 'The AI provider rejected the request.',
                );
            }

            switch (parsed.type) {
                case 'response.output_text.delta': {
                    if (typeof parsed.delta === 'string') {
                        accumulatedText += parsed.delta;
                        onChunk?.(accumulatedText);
                    }
                    break;
                }
                case 'response.reasoning_summary_text.delta': {
                    if (typeof parsed.delta === 'string') {
                        accumulatedReasoning += parsed.delta;
                        onThinking?.(accumulatedReasoning);
                    }
                    break;
                }
                case 'response.completed':
                case 'response.incomplete': {
                    finalPayload = parsed.response as Record<string, unknown> | undefined;
                    break;
                }
                case 'response.failed': {
                    const failed = parsed.response as { error?: { message?: string } } | undefined;
                    throw new Error(
                        `Responses API stream failed: ${failed?.error?.message ?? 'unknown error'}`,
                    );
                }
                // Other events (output_item.*, content_part.*, *.done) add nothing
                // beyond what response.completed already carries.
            }
        };

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                // Responses API SSE: `event: <type>\ndata: <json>\n\n`. The type is
                // also on the data payload, so only `data:` needs reading.
                const events = buffer.split('\n\n');
                buffer = events.pop() || '';

                for (const evt of events) {
                    for (const rawLine of evt.split('\n')) {
                        const line = rawLine.trim();
                        if (line.startsWith('data:')) {
                            handleEvent(line.slice(5).trim());
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock();
        }

        if (!finalPayload) {
            throw new StreamInterruptedError(
                'Connection to AI provider was interrupted. Please try again.',
            );
        }

        // onThinking already fired for the streamed summary; don't replay it.
        return this.parseResponsePayload(
            finalPayload,
            accumulatedReasoning ? undefined : onThinking,
        );
    }

    private parseResponsePayload(
        data: Record<string, unknown>,
        onThinking?: (reasoning: string) => void,
    ): LLMResponse {
        const outputArray = data.output || data;

        if (!Array.isArray(outputArray)) {
            throw new Error('Expected Responses API output to be an array of items');
        }

        let content = '';
        let toolCalls: ToolCall[] | undefined;
        let codeInterpreterOutputs: CodeInterpreterOutput[] | undefined;

        // Track files from annotations (container_file_citation)
        const filesFromAnnotations: Map<string, CodeInterpreterFile[]> = new Map();

        // Process ALL message items (there can be multiple - before and after code_interpreter_call)
        const messageItems = outputArray.filter(
            (item: Record<string, unknown>) => item.type === 'message',
        );
        for (const messageItem of messageItems) {
            const messageContent = messageItem.content as
                | Array<Record<string, unknown>>
                | undefined;
            if (messageContent && Array.isArray(messageContent)) {
                const textParts = messageContent
                    .filter((part) => part.type === 'output_text')
                    .map((part) => part.text as string);
                content += textParts.join('');

                // Extract file citations from annotations
                for (const part of messageContent) {
                    if (part.type === 'output_text' && Array.isArray(part.annotations)) {
                        for (const annotation of part.annotations as Array<
                            Record<string, unknown>
                        >) {
                            if (annotation.type === 'container_file_citation') {
                                const containerId = annotation.container_id as string;
                                const file: CodeInterpreterFile = {
                                    file_id: annotation.file_id as string,
                                    filename: annotation.filename as string,
                                    mime_type: annotation.mime_type as string | undefined,
                                };
                                if (!filesFromAnnotations.has(containerId)) {
                                    filesFromAnnotations.set(containerId, []);
                                }
                                filesFromAnnotations.get(containerId)!.push(file);
                            }
                        }
                    }
                }
            }
        }

        // Capture reasoning items verbatim (id + encrypted_content + summary) so
        // they can be threaded back into the next request — otherwise the model
        // loses its chain-of-thought across the tool-call loop and loops.
        const reasoningItems = outputArray.filter(
            (item: Record<string, unknown>) => item.type === 'reasoning',
        ) as Array<Record<string, unknown>>;

        // A compaction item means the server summarised the input before it this turn.
        // Kept verbatim (id + encrypted_content) so it can replace that input next request.
        const compactionItems = outputArray.filter(
            (item: Record<string, unknown>) => item.type === 'compaction',
        ) as Array<Record<string, unknown>>;

        const summaryText = reasoningItems
            .flatMap((item) => (Array.isArray(item.summary) ? item.summary : []))
            .filter(
                (part): part is { type: string; text: string } =>
                    !!part &&
                    (part as Record<string, unknown>).type === 'summary_text' &&
                    typeof (part as Record<string, unknown>).text === 'string',
            )
            .map((part) => part.text)
            .join('\n\n');
        if (onThinking && summaryText) {
            onThinking(summaryText);
        }

        const functionCallItems = outputArray.filter(
            (item: Record<string, unknown>) => item.type === 'function_call',
        );
        if (functionCallItems.length > 0) {
            toolCalls = functionCallItems.map((item: Record<string, unknown>) => ({
                id: item.call_id as string,
                type: 'function' as const,
                function: {
                    name: item.name as string,
                    arguments: item.arguments as string,
                },
            }));
        }

        // Parse code interpreter outputs
        const codeInterpreterItems = outputArray.filter(
            (item: Record<string, unknown>) => item.type === 'code_interpreter_call',
        );
        if (codeInterpreterItems.length > 0) {
            codeInterpreterOutputs = codeInterpreterItems.map(
                (item: Record<string, unknown>): CodeInterpreterOutput => {
                    const containerId = item.container_id as string;
                    const code = item.code as string | undefined;
                    const outputs = item.outputs as Array<Record<string, unknown>> | undefined;

                    let logs: string | undefined;
                    let files: CodeInterpreterFile[] | undefined;

                    if (outputs && Array.isArray(outputs)) {
                        for (const output of outputs) {
                            if (output.type === 'logs' && output.logs) {
                                logs = (logs || '') + (output.logs as string);
                            }
                            if (output.type === 'files' && Array.isArray(output.files)) {
                                const outputFiles = output.files as Array<Record<string, unknown>>;
                                files = (files || []).concat(
                                    outputFiles.map((f) => ({
                                        file_id: f.file_id as string,
                                        filename: f.filename as string,
                                        mime_type: f.mime_type as string | undefined,
                                    })),
                                );
                            }
                        }
                    }

                    // Merge files from annotations (container_file_citation)
                    const annotationFiles = filesFromAnnotations.get(containerId);
                    if (annotationFiles && annotationFiles.length > 0) {
                        files = (files || []).concat(annotationFiles);
                    }

                    return { container_id: containerId, code, logs, files };
                },
            );
        }

        // If we have file annotations but no code_interpreter_call items, create outputs from annotations
        if (
            (!codeInterpreterOutputs || codeInterpreterOutputs.length === 0) &&
            filesFromAnnotations.size > 0
        ) {
            codeInterpreterOutputs = Array.from(filesFromAnnotations.entries()).map(
                ([containerId, files]) => ({
                    container_id: containerId,
                    files,
                }),
            );
        }

        return {
            content: content || '',
            tool_calls: toolCalls,
            code_interpreter_outputs: codeInterpreterOutputs,
            openai_response_id: data.id as string | undefined,
            usage: normalizeResponsesUsage(data.usage ?? {}),
            reasoning_items: reasoningItems.length > 0 ? reasoningItems : undefined,
            compaction_items: compactionItems.length > 0 ? compactionItems : undefined,
            reasoning_summary: summaryText || undefined,
        };
    }
}

// =============================================================================
// Anthropic Messages API Adapter
// =============================================================================

// Thinking is billed against this ceiling too, and composition tools send a whole
// HTML document as one tool argument — on a large scene the two together overrun a
// smaller budget, costing the whole turn.
const ANTHROPIC_MAX_TOKENS_DEFAULT = 32768;

/**
 * Claude's effort vocabulary, keyed by ours. The two lowest levels collapse onto
 * `low`: Anthropic's guidance for callers that don't want thinking is adaptive at
 * `low` rather than `disabled`.
 */
const ANTHROPIC_EFFORT_BY_REASONING_EFFORT: Record<ReasoningEffort, string> = {
    [ReasoningEffort.NONE]: 'low',
    [ReasoningEffort.MINIMAL]: 'low',
    [ReasoningEffort.LOW]: 'low',
    [ReasoningEffort.MEDIUM]: 'medium',
    [ReasoningEffort.HIGH]: 'high',
    [ReasoningEffort.XHIGH]: 'xhigh',
};

const ANTHROPIC_DEFAULT_EFFORT = 'medium';

function anthropicEffort(options?: LLMClientOptions): string {
    const declared = options?.reasoningEffort as ReasoningEffort | undefined;
    return (declared && ANTHROPIC_EFFORT_BY_REASONING_EFFORT[declared]) || ANTHROPIC_DEFAULT_EFFORT;
}

function toAnthropicContentBlock(part: TextContent | ImageContent): Record<string, unknown> {
    if (part.type === 'text') {
        return { type: 'text', text: part.text };
    }
    const url = part.image_url.url;
    if (url.startsWith('data:')) {
        const match = /^data:([^;,]+);base64,(.+)$/.exec(url);
        if (!match) {
            throw new Error('Anthropic adapter: data URL must be base64-encoded with a media type');
        }
        return {
            type: 'image',
            source: { type: 'base64', media_type: match[1], data: match[2] },
        };
    }
    return { type: 'image', source: { type: 'url', url } };
}

// Anthropic caches by prefix match, in render order tools → system → messages.
// One breakpoint on the system block also caches the tools that render before
// it; trailing breakpoints on the messages let each request read the prior
// conversation prefix from cache. 5-minute ephemeral TTL (the default) suits
// agentic tool loops and back-to-back turns.
const EPHEMERAL_CACHE_CONTROL = { type: 'ephemeral' };

// Mark a message's last block as a cache breakpoint. A string body is promoted
// to a text block so there's a block to carry cache_control.
function markMessageCacheBreakpoint(message: Record<string, unknown>): void {
    const content = message.content;
    if (typeof content === 'string') {
        if (content.length === 0) return;
        message.content = [{ type: 'text', text: content, cache_control: EPHEMERAL_CACHE_CONTROL }];
        return;
    }
    if (Array.isArray(content) && content.length > 0) {
        const lastBlock = content[content.length - 1] as Record<string, unknown>;
        lastBlock.cache_control = EPHEMERAL_CACHE_CONTROL;
    }
}

export class AnthropicMessagesAdapter implements APIAdapter {
    getEndpoint(): string {
        return '/messages';
    }

    buildPayload(
        model: string,
        messages: ConversationMessage[],
        tools: ToolDefinition[] | undefined,
        task: string | undefined,
        streaming: boolean,
        baseUrl: string,
        options?: LLMClientOptions,
    ): Record<string, unknown> {
        // Adaptive thinking is opt-in (Codeo) and only on models that support it.
        // Gating both the request param and block replay on the same flag means a
        // caller that doesn't opt in (or a switch to a non-thinking model like
        // Haiku) never sends stray thinking blocks the model would reject.
        const thinkingEnabled =
            modelSupportsAdaptiveThinking(model) && !!options?.enableThinkingSummaries;

        // Anthropic separates system from messages and uses typed content blocks.
        const systemParts: string[] = [];
        const anthropicMessages: Array<Record<string, unknown>> = [];

        for (const msg of messages) {
            if (msg.role === 'system') {
                if (typeof msg.content === 'string' && msg.content.length > 0) {
                    systemParts.push(msg.content);
                }
                continue;
            }
            if (isAssistantMessage(msg)) {
                const blocks: Array<Record<string, unknown>> = [];
                // Thinking blocks must come first and be replayed unmodified —
                // Anthropic rejects a turn that drops the thinking blocks that
                // preceded a tool_use when thinking is enabled.
                if (thinkingEnabled && msg.thinking_blocks) {
                    for (const tb of msg.thinking_blocks) {
                        if (tb.type === 'thinking') {
                            blocks.push({
                                type: 'thinking',
                                thinking: tb.thinking,
                                signature: tb.signature,
                            });
                        } else {
                            blocks.push({ type: 'redacted_thinking', data: tb.data });
                        }
                    }
                }
                if (typeof msg.content === 'string' && msg.content.length > 0) {
                    blocks.push({ type: 'text', text: msg.content });
                }
                if (msg.tool_calls) {
                    for (const tc of msg.tool_calls) {
                        let parsedInput: unknown = {};
                        try {
                            parsedInput = tc.function.arguments
                                ? JSON.parse(tc.function.arguments)
                                : {};
                        } catch {
                            parsedInput = {};
                        }
                        blocks.push({
                            type: 'tool_use',
                            id: tc.id,
                            name: tc.function.name,
                            input: parsedInput,
                        });
                    }
                }
                if (blocks.length > 0) {
                    anthropicMessages.push({ role: 'assistant', content: blocks });
                }
                continue;
            }
            if (isToolMessage(msg)) {
                const toolResultContent: string | Array<Record<string, unknown>> =
                    typeof msg.content === 'string'
                        ? msg.content
                        : msg.content.map(toAnthropicContentBlock);
                anthropicMessages.push({
                    role: 'user',
                    content: [
                        {
                            type: 'tool_result',
                            tool_use_id: msg.tool_call_id,
                            content: toolResultContent,
                        },
                    ],
                });
                continue;
            }
            // Default: user message. Convert content array (text + image_url parts) to typed Anthropic blocks.
            if (typeof msg.content === 'string') {
                anthropicMessages.push({ role: 'user', content: msg.content });
            } else {
                anthropicMessages.push({
                    role: 'user',
                    content: msg.content.map(toAnthropicContentBlock),
                });
            }
        }

        const payload: Record<string, unknown> = {
            model,
            messages: anthropicMessages,
            max_tokens: ANTHROPIC_MAX_TOKENS_DEFAULT,
        };

        // Gated on the model rather than on `thinkingEnabled`: these models think by
        // default, so omitting output_config doesn't mean "no thinking" — it means
        // the API's own default of 'high'.
        if (modelSupportsAdaptiveThinking(model)) {
            payload.output_config = { effort: anthropicEffort(options) };
        }

        // Opt-in, since the default display omits the summary and reads as a long
        // silent pause. The same flag gates thinking-block replay above.
        if (thinkingEnabled) {
            payload.thinking = { type: 'adaptive', display: 'summarized' };
        }

        if (systemParts.length > 0) {
            payload.system = [
                {
                    type: 'text',
                    text: systemParts.join('\n\n'),
                    cache_control: EPHEMERAL_CACHE_CONTROL,
                },
            ];
        }

        if (streaming) {
            payload.stream = true;
        }

        if (task && baseUrl.includes('chatterway')) {
            payload.task = task;
        }

        if (tools && tools.length > 0) {
            payload.tools = tools.map((t) => ({
                name: t.function.name,
                description: t.function.description,
                input_schema: t.function.parameters,
            }));
        }

        // Breakpoint the two most recent messages so each request reads the
        // prior prefix from cache. Two markers (not one) keep the previous
        // breakpoint within Anthropic's 20-block lookback window even when a
        // turn appends many blocks (e.g. a tool result with several images).
        const lastIndex = anthropicMessages.length - 1;
        if (lastIndex >= 0) {
            markMessageCacheBreakpoint(anthropicMessages[lastIndex]);
        }
        if (lastIndex - 1 >= 0) {
            markMessageCacheBreakpoint(anthropicMessages[lastIndex - 1]);
        }

        return payload;
    }

    async parseResponse(
        response: Response,
        streaming: boolean,
        onChunk?: (content: string) => void,
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        if (streaming) {
            return this.parseStreamingResponse(response, onChunk!, onThinking);
        }
        return this.parseJsonResponse(response);
    }

    private async parseJsonResponse(response: Response): Promise<LLMResponse> {
        const data = await response.json();
        let content = '';
        const toolCalls: ToolCall[] = [];
        const thinkingBlocks: ReasoningBlock[] = [];
        for (const block of data.content || []) {
            switch (block.type) {
                case 'text': {
                    content += block.text;

                    break;
                }
                case 'thinking': {
                    thinkingBlocks.push({
                        type: 'thinking',
                        thinking: block.thinking ?? '',
                        signature: block.signature ?? '',
                    });

                    break;
                }
                case 'redacted_thinking': {
                    thinkingBlocks.push({ type: 'redacted_thinking', data: block.data ?? '' });

                    break;
                }
                case 'tool_use': {
                    toolCalls.push({
                        id: block.id,
                        type: 'function',
                        function: {
                            name: block.name,
                            arguments: JSON.stringify(block.input ?? {}),
                        },
                    });

                    break;
                }
                // No default
            }
        }
        // input_tokens is the uncached, full-price remainder; the cached prefix
        // is reported separately in cache_read/cache_creation.
        const inputTokens = data.usage?.input_tokens || 0;
        const outputTokens = data.usage?.output_tokens || 0;
        return {
            content,
            tool_calls: toolCalls.length > 0 ? toolCalls : undefined,
            stop_reason: data.stop_reason as string | undefined,
            thinking_blocks: thinkingBlocks.length > 0 ? thinkingBlocks : undefined,
            usage: {
                input_tokens: inputTokens,
                output_tokens: outputTokens,
                total_tokens: inputTokens + outputTokens,
                cache_creation_input_tokens: data.usage?.cache_creation_input_tokens || 0,
                cache_read_input_tokens: data.usage?.cache_read_input_tokens || 0,
                cache_creation_1h_input_tokens:
                    data.usage?.cache_creation?.ephemeral_1h_input_tokens || 0,
            },
        };
    }

    private async parseStreamingResponse(
        response: Response,
        onChunk: (content: string) => void,
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        if (!response.body) {
            throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedText = '';
        let accumulatedThinking = '';
        let inputTokens = 0;
        let outputTokens = 0;
        let cacheCreationTokens = 0;
        let cacheReadTokens = 0;
        let cacheCreation1hTokens = 0;
        let stopReason: string | undefined;

        // Each content block is keyed by index. text blocks accumulate `text`,
        // tool_use blocks accumulate `inputJson` (a partial JSON string the
        // server streams in pieces) plus their static id/name from block_start.
        // thinking blocks accumulate summarized reasoning `text` plus a streamed
        // `signature` that must be replayed unmodified.
        type BlockState =
            | { kind: 'text'; text: string }
            | { kind: 'tool_use'; id: string; name: string; inputJson: string }
            | { kind: 'thinking'; thinking: string; signature: string }
            | { kind: 'redacted_thinking'; data: string };
        const blocks: Map<number, BlockState> = new Map();

        const handleEvent = (eventType: string, data: string) => {
            if (!data) return;
            let parsed: Record<string, unknown>;
            try {
                parsed = JSON.parse(data);
            } catch {
                return;
            }

            // Backend may inject a synthetic stream_interrupted error event.
            const errorObj = parsed.error as { type?: string; message?: string } | undefined;
            if (errorObj?.type === 'stream_interrupted') {
                throw new StreamInterruptedError(
                    'Connection to AI provider was interrupted. Please try again.',
                );
            }
            if (errorObj) {
                throw new ProviderStreamError(
                    errorObj.message ?? 'The AI provider rejected the request.',
                );
            }

            switch (eventType) {
                case 'message_start': {
                    const message = parsed.message as {
                        usage?: {
                            input_tokens?: number;
                            cache_creation_input_tokens?: number;
                            cache_read_input_tokens?: number;
                            cache_creation?: { ephemeral_1h_input_tokens?: number };
                        };
                    };
                    if (message?.usage?.input_tokens) {
                        inputTokens = message.usage.input_tokens;
                    }
                    if (message?.usage?.cache_creation_input_tokens) {
                        cacheCreationTokens = message.usage.cache_creation_input_tokens;
                    }
                    if (message?.usage?.cache_read_input_tokens) {
                        cacheReadTokens = message.usage.cache_read_input_tokens;
                    }
                    if (message?.usage?.cache_creation?.ephemeral_1h_input_tokens) {
                        cacheCreation1hTokens =
                            message.usage.cache_creation.ephemeral_1h_input_tokens;
                    }
                    break;
                }
                case 'content_block_start': {
                    const idx = parsed.index as number;
                    const block = parsed.content_block as Record<string, unknown>;
                    switch (block?.type) {
                        case 'text': {
                            blocks.set(idx, { kind: 'text', text: '' });

                            break;
                        }
                        case 'tool_use': {
                            blocks.set(idx, {
                                kind: 'tool_use',
                                id: stringifyUnknown(block.id),
                                name: stringifyUnknown(block.name),
                                inputJson: '',
                            });

                            break;
                        }
                        case 'thinking': {
                            blocks.set(idx, {
                                kind: 'thinking',
                                thinking: stringifyUnknown(block.thinking),
                                signature: stringifyUnknown(block.signature),
                            });

                            break;
                        }
                        case 'redacted_thinking': {
                            blocks.set(idx, {
                                kind: 'redacted_thinking',
                                data: stringifyUnknown(block.data),
                            });

                            break;
                        }
                        // No default
                    }
                    break;
                }
                case 'content_block_delta': {
                    const idx = parsed.index as number;
                    const delta = parsed.delta as {
                        type?: string;
                        text?: string;
                        partial_json?: string;
                        thinking?: string;
                        signature?: string;
                    };
                    const block = blocks.get(idx);
                    if (!block) return;
                    if (delta.type === 'text_delta' && block.kind === 'text' && delta.text) {
                        block.text += delta.text;
                        accumulatedText += delta.text;
                        onChunk(accumulatedText);
                    } else if (
                        delta.type === 'input_json_delta' &&
                        block.kind === 'tool_use' &&
                        delta.partial_json
                    ) {
                        block.inputJson += delta.partial_json;
                    } else if (
                        delta.type === 'thinking_delta' &&
                        block.kind === 'thinking' &&
                        delta.thinking
                    ) {
                        block.thinking += delta.thinking;
                        accumulatedThinking += delta.thinking;
                        onThinking?.(accumulatedThinking);
                    } else if (
                        delta.type === 'signature_delta' &&
                        block.kind === 'thinking' &&
                        delta.signature
                    ) {
                        block.signature += delta.signature;
                    }
                    break;
                }
                case 'message_delta': {
                    const usage = parsed.usage as { output_tokens?: number } | undefined;
                    if (usage?.output_tokens) {
                        outputTokens = usage.output_tokens;
                    }
                    const delta = parsed.delta as { stop_reason?: string } | undefined;
                    if (delta?.stop_reason) {
                        stopReason = delta.stop_reason;
                    }
                    break;
                }
                // content_block_stop / message_stop / ping: nothing to do.
            }
        };

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                // Anthropic SSE: each event is `event: <type>\ndata: <json>\n\n`.
                const events = buffer.split('\n\n');
                buffer = events.pop() || '';

                for (const evt of events) {
                    let eventType = '';
                    let data = '';
                    for (const rawLine of evt.split('\n')) {
                        const line = rawLine.trim();
                        if (line.startsWith('event:')) {
                            eventType = line.slice(6).trim();
                        } else if (line.startsWith('data:')) {
                            data = line.slice(5).trim();
                        }
                    }
                    if (eventType) handleEvent(eventType, data);
                }
            }
        } finally {
            reader.releaseLock();
        }

        const toolCalls: ToolCall[] = [];
        const thinkingBlocks: ReasoningBlock[] = [];
        // Iterate by index order so tool_calls (and replayed thinking blocks)
        // match their on-wire arrival.
        const sortedIndices = Array.from(blocks.keys()).sort((a, b) => a - b);
        for (const idx of sortedIndices) {
            const block = blocks.get(idx);
            switch (block?.kind) {
                case 'tool_use': {
                    toolCalls.push({
                        id: block.id,
                        type: 'function',
                        function: {
                            name: block.name,
                            // inputJson may be empty for parameterless tools — emit "{}" so callers can JSON.parse.
                            arguments: block.inputJson || '{}',
                        },
                    });

                    break;
                }
                case 'thinking': {
                    thinkingBlocks.push({
                        type: 'thinking',
                        thinking: block.thinking,
                        signature: block.signature,
                    });

                    break;
                }
                case 'redacted_thinking': {
                    thinkingBlocks.push({ type: 'redacted_thinking', data: block.data });

                    break;
                }
                // No default
            }
        }

        return {
            content: accumulatedText,
            tool_calls: toolCalls.length > 0 ? toolCalls : undefined,
            stop_reason: stopReason,
            thinking_blocks: thinkingBlocks.length > 0 ? thinkingBlocks : undefined,
            usage: {
                input_tokens: inputTokens,
                output_tokens: outputTokens,
                total_tokens: inputTokens + outputTokens,
                cache_creation_input_tokens: cacheCreationTokens,
                cache_read_input_tokens: cacheReadTokens,
                cache_creation_1h_input_tokens: cacheCreation1hTokens,
            },
        };
    }
}

// =============================================================================
// Adapter Factory
// =============================================================================

function getAdapterForModel(model: string, options?: LLMClientOptions): APIAdapter {
    if (model.startsWith('claude-')) {
        return new AnthropicMessagesAdapter();
    }
    if (shouldUseResponsesApi(model, options?.reasoningEffort)) {
        return new ResponsesAPIAdapter();
    }
    return new ChatCompletionsAdapter();
}

// =============================================================================
// LLM Client
// =============================================================================

const TRUNCATION_NOTICE =
    'Your response was cut off at the output token limit before it finished. Anything ' +
    'left incomplete — including a tool call whose arguments never closed — was ' +
    'discarded. Continue with a smaller payload: send only what has to change rather ' +
    'than rewriting everything.';

/**
 * Tells the model why its turn ended early. Adapters report `stop_reason` and drop
 * what the limit left unusable; what to say about it is the client's call, and
 * saying it once here keeps the wording the same across providers.
 */
function withTruncationNotice(response: LLMResponse): LLMResponse {
    if (response.stop_reason !== 'max_tokens') {
        return response;
    }
    return {
        ...response,
        content: [response.content, TRUNCATION_NOTICE].filter(Boolean).join('\n\n'),
    };
}

export class LLMClient {
    private baseUrl: string;
    private apiKey: string | null;
    private model: string;
    private adapter: APIAdapter;
    private options: LLMClientOptions;

    constructor(apiKey: string | null, model: string, options?: LLMClientOptions) {
        this.apiKey = apiKey;
        this.model = model;
        this.options = options || {};
        this.adapter = getAdapterForModel(model, this.options);

        let envBaseUrl: string | undefined;
        try {
            envBaseUrl = process.env.OPENAI_BASE_URL;
        } catch {
            // process not defined (browser without DefinePlugin)
        }
        this.baseUrl = this.options.baseUrl || envBaseUrl || '/api/chatterway/v1';
    }

    public async complete(
        messages: ConversationMessage[],
        tools: ToolDefinition[] | undefined,
        abortSignal: AbortSignal | undefined,
        task: string,
        options?: { responseFormat?: ResponseFormat },
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        return this.executeRequest(
            messages,
            tools,
            abortSignal,
            task,
            false,
            undefined,
            options,
            onThinking,
        );
    }

    public getApiKey(): string | null {
        return this.apiKey;
    }

    public getModel(): string {
        return this.model;
    }

    /**
     * The compaction threshold this client's requests carry, or undefined on paths that send
     * no `context_management` (Chat Completions, Anthropic, non-gpt-5 models).
     */
    public getEffectiveCompactionThreshold(): number | undefined {
        if (!(this.adapter instanceof ResponsesAPIAdapter) || !isOpenAIReasoningModel(this.model)) {
            return undefined;
        }
        return this.options.compactionThreshold ?? getCompactionThreshold(this.model);
    }

    public async completeStreaming(
        messages: ConversationMessage[],
        onChunk: (content: string) => void,
        tools: ToolDefinition[] | undefined,
        abortSignal: AbortSignal | undefined,
        task: string,
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        return this.executeRequest(
            messages,
            tools,
            abortSignal,
            task,
            true,
            onChunk,
            undefined,
            onThinking,
        );
    }

    private async buildHeaders(): Promise<Record<string, string>> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (this.apiKey) {
            headers.Authorization = `Bearer ${this.apiKey}`;
        }

        // CSRF + Turnstile headers are for our same-origin chatterway proxy. A direct provider
        // base URL (--direct / --base-url to OpenAI, OpenRouter, etc.) makes this a cross-origin
        // request, where these non-safelisted headers force a CORS preflight the provider may
        // reject — OpenRouter does, which fails every request with "Failed to fetch". So only
        // attach them when routing through chatterway.
        if (this.baseUrl.includes('chatterway')) {
            headers['X-CSRFToken'] = getCSRFToken();
            const turnstileToken = await awaitTurnstileToken();
            if (turnstileToken) {
                headers['X-Turnstile-Token'] = turnstileToken;
            }
            headers['X-Turnstile-Diagnostic'] = getTurnstileDiagnosticHeader();
        }

        return headers;
    }

    private async handleErrorResponse(response: Response, task?: string): Promise<void> {
        if (response.status === 429) {
            const retryAfter = response.headers.get('Retry-After');
            // The usage limiter labels its headers X-RateLimit-Cost-* (and previously
            // X-RateLimit-Tokens-*); the per-view request limiter emits plain
            // X-RateLimit-* with neither word. Both spellings are matched so this keeps
            // classifying correctly while a mix of backend versions is deployed.
            const hasUsageHeader = Array.from(response.headers.keys()).some((key) => {
                const lowerKey = key.toLowerCase();
                return lowerKey.includes('ratelimit-cost') || lowerKey.includes('ratelimit-tokens');
            });
            const limitType = hasUsageHeader ? 'token' : 'request';

            // Determine priority from response header, fall back to task classification
            const priorityHeader = response.headers.get('X-RateLimit-Priority');
            const priority: RateLimitPriority =
                priorityHeader === 'background' || priorityHeader === 'interactive'
                    ? priorityHeader
                    : getTaskPriority(task);

            analytics.track('LLM Rate Limit Hit', {
                model: this.model,
                task: task || 'unknown',
                limit_type: limitType,
                priority,
                retry_after_seconds: retryAfter ? parseInt(retryAfter, 10) : null,
                has_retry_after: !!retryAfter,
            });

            if (retryAfter) {
                const seconds = parseInt(retryAfter, 10);
                setRateLimit(seconds, priority);
                throw new RateLimitError(formatRateLimitMessage(seconds, formatRetryTime));
            } else {
                throw new RateLimitError('Rate limit exceeded. Please try again later.');
            }
        }

        let errorDetails = '';
        try {
            const responseText = await response.text();
            if (responseText) {
                try {
                    const errorJson = JSON.parse(responseText);
                    errorDetails = JSON.stringify(errorJson);

                    if (errorJson?.error_code === ErrorEnum.AI_CONTENT_VIOLATION) {
                        throw new ContentViolationError(
                            typeof errorJson.error === 'string'
                                ? errorJson.error
                                : 'This request does not meet our content policy.',
                            errorJson.blocked === true,
                        );
                    }

                    // Detect context length exceeded errors (safety net — truncation should prevent most)
                    if (
                        response.status === 400 &&
                        (errorJson?.error?.code === 'context_length_exceeded' ||
                            (typeof errorJson?.error?.message === 'string' &&
                                errorJson.error.message
                                    .toLowerCase()
                                    .includes('maximum context length')))
                    ) {
                        analytics.track('LLM Context Length Exceeded', {
                            model: this.model,
                            task: task || 'unknown',
                        });
                        throw new ContextLengthExceededError(
                            'The conversation has become too long. Please clear the chat to continue.',
                        );
                    }
                } catch (e) {
                    if (
                        e instanceof ContextLengthExceededError ||
                        e instanceof ContentViolationError
                    ) {
                        throw e;
                    }
                    errorDetails = responseText;
                }
            }
        } catch (e) {
            if (e instanceof ContextLengthExceededError || e instanceof ContentViolationError) {
                throw e;
            }
            // Failed to read response body
        }

        const errorMessage = `LLM API Error: ${response.status} ${response.statusText}${errorDetails ? ` - ${errorDetails}` : ''}`;

        ErrorReporter.capture(new Error(errorMessage), {
            extra: {
                model: this.model,
                task: task || 'unknown',
                status: response.status,
                statusText: response.statusText,
                errorDetails,
            },
        });

        let isTestEnv = false;
        try {
            isTestEnv = process.env.NODE_ENV === 'test';
        } catch {
            // process.env not available
        }

        if (isTestEnv) {
            throw new Error(errorMessage);
        } else {
            throw new Error('Sorry, an error occurred. Please try again.');
        }
    }

    private async executeRequest(
        messages: ConversationMessage[],
        tools?: ToolDefinition[],
        abortSignal?: AbortSignal,
        task?: string,
        streaming: boolean = false,
        onChunk?: (content: string) => void,
        options?: { responseFormat?: ResponseFormat },
        onThinking?: (reasoning: string) => void,
    ): Promise<LLMResponse> {
        if (process.env.NODE_ENV === 'development' && !task) {
            const caller = new Error().stack?.split('\n')[2]?.trim() || 'unknown caller';
            Toast.info(`LLM call missing task tag: ${caller}`, 'Missing task tag');
        }

        // Check if already rate limited before making request (only for this task's tier)
        const taskPriority = getTaskPriority(task);
        if (isRateLimited(taskPriority)) {
            const timeUntilSeconds = Math.ceil((getTimeUntilRetry(taskPriority) || 0) / 1000);

            // Track blocked attempts so we have visibility into users hitting cached rate limits
            analytics.track('LLM Rate Limit Blocked (Preflight)', {
                model: this.model,
                task: task || 'unknown',
                priority: taskPriority,
                time_remaining_seconds: timeUntilSeconds,
            });

            throw new RateLimitError(formatRateLimitMessage(timeUntilSeconds, formatRetryTime));
        }

        const endpoint = this.adapter.getEndpoint();
        const payload = this.adapter.buildPayload(
            this.model,
            messages,
            tools,
            task,
            streaming,
            this.baseUrl,
            this.options,
            options?.responseFormat,
        );

        // Apply temperature override if set (shared across all adapters)
        this.applyTemperatureOverride(payload);

        const transportBody = JSON.stringify(encodeScriptStringsForTransport(payload));

        const headers = await this.buildHeaders();

        // Retry config for 503 errors
        const maxRetries = 2;
        const baseDelayMs = 2000;
        let lastError: Error | null = null;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            if (abortSignal?.aborted) {
                throw new DOMException('Request aborted', 'AbortError');
            }

            try {
                const response = await fetch(`${this.baseUrl}${endpoint}`, {
                    method: 'POST',
                    headers,
                    body: transportBody,
                    signal: abortSignal,
                });

                if (response.status === 503 && attempt < maxRetries) {
                    const delayMs = baseDelayMs * Math.pow(2, attempt);
                    analytics.track('LLM 503 Retry', {
                        model: this.model,
                        task: task || 'unknown',
                        attempt: attempt + 1,
                        delay_ms: delayMs,
                    });
                    await this.delay(delayMs, abortSignal);
                    continue;
                }

                if (!response.ok) {
                    await this.handleErrorResponse(response, task);
                }

                return withTruncationNotice(
                    await this.adapter.parseResponse(response, streaming, onChunk, onThinking),
                );
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    throw error;
                }
                lastError = error instanceof Error ? error : new Error(String(error));

                if (attempt < maxRetries && this.isRetryableNetworkError(error)) {
                    const delayMs = baseDelayMs * Math.pow(2, attempt);
                    await this.delay(delayMs, abortSignal);
                    continue;
                }

                throw lastError;
            }
        }

        throw lastError || new Error('Request failed after retries');
    }

    private applyTemperatureOverride(payload: Record<string, unknown>): void {
        try {
            const tempEnv = process.env.EVAL_TEMPERATURE;
            if (tempEnv && tempEnv !== '') {
                const temperature = parseFloat(tempEnv);
                if (!isNaN(temperature)) {
                    payload.temperature = temperature;
                }
            }
        } catch {
            // process.env not available or parsing failed
        }
    }

    private delay(ms: number, abortSignal?: AbortSignal): Promise<void> {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(resolve, ms);
            if (abortSignal) {
                abortSignal.addEventListener(
                    'abort',
                    () => {
                        clearTimeout(timeout);
                        reject(new DOMException('Request aborted', 'AbortError'));
                    },
                    { once: true },
                );
            }
        });
    }

    private isRetryableNetworkError(error: unknown): boolean {
        if (!(error instanceof Error)) return false;
        return error.name === 'TypeError' || error.message.includes('network');
    }
}
